import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetSaleList,
  GetSaleDetail,
  CreateSale,
  UpdateSale,
  DeleteSale,
  CancelSale,
} from "../../Services/SaleService";

/**
 * Sale State Interface with caching and optimization
 */
export interface SaleState {
  // List data
  saleCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  
  // Detail data
  selectedSaleDetail: any | null;
  
  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  
  // Error states
  error: string | null;
  errorDetail: string | null;
  
  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  
  // Cache management
  cache: Map<string, CacheEntry>;
  requestAbortController: AbortController | null;
  lastFetchParams: FetchParams | null;
  
  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number; // milliseconds
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  financialYear: string;
  saleType: string;
  startDate: string;
  endDate: string;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Generate cache key based on fetch parameters
 */
const generateCacheKey = (params: FetchParams): string => {
  return `sale_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.saleType}`;
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

/**
 * Async Thunk: Fetch sale list with pagination and search
 */
export const fetchSaleList = createAsyncThunk(
  "sale/fetchSaleList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      financialYear: string;
      saleType: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { sale: SaleState };
      const cacheKey = generateCacheKey(params);
      
      // Check if we have valid cache
      if (root.sale.cache.has(cacheKey)) {
        const cached = root.sale.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);
      urlParams.append("saleType", params.saleType);
      urlParams.append("startDate", params.startDate);
      urlParams.append("endDate", params.endDate);

      const result: any = await GetSaleList(urlParams);
      
      if (result?.data?.data?.saleCollection) {
        return result.data.data;
      }
      
      return rejectWithValue("No sale data received");
    } catch (error: any) {
      console.error("Error fetching sale list:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch sales"
      );
    }
  }
);

/**
 * Async Thunk: Fetch single sale detail
 */
export const fetchSaleDetail = createAsyncThunk(
  "sale/fetchSaleDetail",
  async (
    params: {
      consumerID: number;
      firmId: number;
      branchId: number;
      saleID: number;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerID", params.consumerID.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("saleID", params.saleID.toString());
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetSaleDetail(urlParams);
      
      if (result?.data?.data) {
        return result.data.data;
      }
      
      return rejectWithValue("No sale detail received");
    } catch (error: any) {
      console.error("Error fetching sale detail:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch sale details"
      );
    }
  }
);

/**
 * Async Thunk: Create new sale
 */
export const createSaleAsync = createAsyncThunk(
  "sale/createSale",
  async (saleData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateSale(saleData);
      
      if (result?.data) {
        return result.data;
      }
      
      return rejectWithValue("Failed to create sale");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create sale"
      );
    }
  }
);

/**
 * Async Thunk: Update sale
 */
export const updateSaleAsync = createAsyncThunk(
  "sale/updateSale",
  async (saleData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateSale(saleData);
      
      if (result?.data) {
        return result.data;
      }
      
      return rejectWithValue("Failed to update sale");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update sale"
      );
    }
  }
);

/**
 * Async Thunk: Delete sale
 */
export const deleteSaleAsync = createAsyncThunk(
  "sale/deleteSale",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteSale(deleteData);
      
      if (result?.data) {
        return deleteData;
      }
      
      return rejectWithValue("Failed to delete sale");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete sale"
      );
    }
  }
);

/**
 * Async Thunk: Cancel sale
 */
export const cancelSaleAsync = createAsyncThunk(
  "sale/cancelSale",
  async (cancelData: any, { rejectWithValue }) => {
    try {
      const result: any = await CancelSale(cancelData);
      
      if (result?.data) {
        return result.data;
      }
      
      return rejectWithValue("Failed to cancel sale");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to cancel sale"
      );
    }
  }
);

const initialState: SaleState = {
  saleCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedSaleDetail: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  requestAbortController: null,
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const saleSlice = createSlice({
  name: "sale",
  initialState,
  reducers: {
    /**
     * Clear all sale data
     */
    clearSaleData: (state) => {
      state.saleCollection = [];
      state.totalCount = 0;
      state.selectedSaleDetail = null;
      state.error = null;
    },

    /**
     * Clear sale detail
     */
    clearSaleDetail: (state) => {
      state.selectedSaleDetail = null;
      state.errorDetail = null;
    },

    /**
     * Clear errors
     */
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },

    /**
     * Clear success flags
     */
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },

    /**
     * Invalidate cache for refetch
     */
    invalidateCache: (state) => {
      state.cache.clear();
    },

    /**
     * Set fetch metadata
     */
    setFetchMetadata: (
      state,
      action: PayloadAction<Partial<FetchParams>>
    ) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    /**
     * Fetch Sale List handlers
     */
    builder
      .addCase(fetchSaleList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(
        fetchSaleList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingList = false;
          state.saleCollection = action.payload?.saleCollection || [];
          state.totalCount = action.payload?.totalCount || 0;
          state.pageIndex = action.payload?.pageIndex || 1;
          state.pageSize = action.payload?.pageSize || 10;
          
          // Update cache
          if (state.lastFetchParams) {
            const cacheKey = generateCacheKey(state.lastFetchParams);
            state.cache.set(cacheKey, {
              data: action.payload,
              timestamp: Date.now(),
              expiresIn: CACHE_DURATION,
            });
          }
        }
      )
      .addCase(fetchSaleList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    /**
     * Fetch Sale Detail handlers
     */
    builder
      .addCase(fetchSaleDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(
        fetchSaleDetail.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingDetail = false;
          state.selectedSaleDetail = action.payload;
        }
      )
      .addCase(fetchSaleDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    /**
     * Create Sale handlers
     */
    builder
      .addCase(createSaleAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createSaleAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createSaleAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    /**
     * Update Sale handlers
     */
    builder
      .addCase(updateSaleAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateSaleAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateSaleAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    /**
     * Delete Sale handlers
     */
    builder
      .addCase(deleteSaleAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteSaleAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteSaleAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });

    /**
     * Cancel Sale handlers
     */
    builder
      .addCase(cancelSaleAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
      })
      .addCase(cancelSaleAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(cancelSaleAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearSaleData,
  clearSaleDetail,
  clearErrors,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = saleSlice.actions;

export default saleSlice.reducer;
